import { createTheme, ThemeOptions, responsiveFontSizes, alpha } from '@mui/material/styles';
import { red, blue, grey } from '@mui/material/colors';
import texturedOverlay from '../assets/images/textured_overlay_1.png';
import appBarBackground from '../assets/images/app_bar_background.png';

// Comprehensive theme configuration
const themeOptions: ThemeOptions = {
  // Color palette
  palette: {
    mode: 'light',
    // Updated primary color to red-orange from palette
    primary: {
      main: '#ED1C24',      // Red-orange from palette
      light: '#FF610D',     // Lighter red-orange
      dark: '#C50009',      // Darker red-orange
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#48123B',      // Dark purple from palette
      light: '#990050',     // Lighter aubergine
      dark: '#2D0C28',      // Darker aubergine
      contrastText: '#ffffff',
    },
    error: {
      main: '#990050',      // Light purple repurposed for error
      light: '#B62D76',
      dark: '#7D0042',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FF610D',      // Orange from palette
      light: '#FF8845',     // Lighter orange
      dark: '#D44500',      // Darker orange
      contrastText: '#ffffff',
    },
    info: {
      main: '#3F51B5',      // Indigo blue that complements the purple tones
      light: '#7986CB',
      dark: '#303F9F',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2E7D32',      // Deeper green for better contrast
      light: '#4CAF50',
      dark: '#1B5E20',
      contrastText: '#ffffff',
    },
    grey: {
      50: '#F8F5F9',        // Custom grey with slight purple tint
      100: '#F1EBF2',
      200: '#E4DBE6',
      300: '#D4C5D8',
      400: '#B7A3BD',
      500: '#9A83A1',
      600: '#7C6884',
      700: '#5E4E65',
      800: '#413647',
      900: '#251F29',
      A100: '#D5C8DB',
      A200: '#B19DBB',
      A400: '#7A597F',
      A700: '#593D61',
    },
    text: {
      primary: 'rgba(33, 16, 35, 0.87)',   // Dark text with slight purple tint
      secondary: 'rgba(33, 16, 35, 0.6)',  // Secondary text
      disabled: 'rgba(33, 16, 35, 0.38)',  // Disabled text
    },
    divider: alpha('#48123B', .2),          // Divider - secondary.main
    background: {
      paper: '#ffffff',                    // Clean white for paper
      default: '#EFEFF0',                  // Very light purple tint for background
    },
    action: {
      // active: alpha('#990050', 0.2),
      // hover: alpha('#990050', 0.1),
      // hoverOpacity: 0.1,
      // selected: alpha('#990050', 0.2),
      // selectedOpacity: 0.2,
      disabled: alpha('#EFEFF0', 1),
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      disabledOpacity: 0.38,
      focus: 'rgba(237, 28, 36, 0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },

  // Typography configuration
  typography: {
    fontFamily: [
      '"Gilroy"',
      '"Segoe UI"',
      '-apple-system',
      'BlinkMacSystemFont',
      
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    // Headings: use Gilroy Black (900)
    h1: {
      fontWeight: 900,
      fontSize: '6rem',
      lineHeight: 1.167,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontWeight: 900,
      fontSize: '3.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontWeight: 900,
      fontSize: '3rem',
      lineHeight: 1.167,
      letterSpacing: '0em',
    },
    h4: {
      fontWeight: 900,
      fontSize: '2.125rem',
      lineHeight: 1.235,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontWeight: 900,
      fontSize: '1.5rem',
      lineHeight: 1.334,
      letterSpacing: '0em',
    },
    h6: {
      fontWeight: 900,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'uppercase',
    },
    caption: {
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },

  // Spacing configuration
  spacing: 8, // Base spacing unit in px

  // Breakpoints configuration (responsive design)
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },

  // Transitions configuration
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },

  // Z-index configuration
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },

  // Shape configuration
  shape: {
    borderRadius: 4,
  },

  // Shadow configuration (box-shadow)
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],

  // Components overrides
  components: {
    MuiTypography: {
      styleOverrides: {        
        h1: {
          color: '#48123B',
        },
        h2: {
          color: '#48123B',
        },
        h3: {
          color: '#48123B',
        },
        h4: {
          color: '#48123B',
        },
        h5: {
          color: '#48123B',
        },
        h6: {
          color: '#48123B',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
        containedPrimary: {
          boxShadow: '0 4px 6px rgba(146, 0, 80, 0.25)', // Custom shadow for magenta buttons
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            display: 'none',
          },
          borderRadius: 8,
          overflow: 'hidden',
        },
      },
    },
    // orange to magenta gradient for AppBar with textured overlay
    // MuiAppBar: {
    //   styleOverrides: {
    //     root: {
    //       background: `
    //         linear-gradient(90deg, #f95b07 0%, #950079 100%)
    //         ,url(${texturedOverlay})
    //       `,
    //       backgroundRepeat: 'repeat',
    //       backgroundSize: 'cover', // or 'contain' if you want to avoid tiling
    //       backgroundBlendMode: 'lighten', // or 'screen', 'lighten','hard-light',  etc.
    //     },
    //   },
    // },
    //background image approach to control brand colors
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: `url(${appBarBackground})`,
          backgroundRepeat: 'repeat',        // or 'no-repeat' if you want a single image
          backgroundSize: 'contain',           // or 'contain' if you want to scale the image inside
          backgroundPosition: 'center',      // centers the image
          backgroundColor: 'transparent',    // fallback or default background
        },
      },
    },

    MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: alpha('#EFEFF0', .4),
              '&:hover': {
                backgroundColor: alpha('#EFEFF0', .1),
              }
            },
          },
        },
      },
      
      // Keep your existing MuiMenuItem override
      // MuiMenuItem: {
      //   styleOverrides: {
      //     root: {
      //       '&.Mui-selected': {
      //         backgroundColor: alpha('#ED1C24', 0.2),
      //         '&:hover': {
      //           backgroundColor: alpha('#ED1C24', 0.2),
      //         }
      //       },
      //     },
      //   },
      // },    
  },
};

// Create the theme with all options
const theme = responsiveFontSizes(createTheme(themeOptions));

// Add DataGrid styling through the sx prop instead
// When using DataGridPro in components, apply these styles:
// sx={{
//   '& .MuiDataGrid-root': { border: 'none' },
//   '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
//   '& .MuiDataGrid-columnHeader': { fontSize: '0.875rem', fontWeight: 600 }
// }}

export default theme;